import {Link, type ErrorComponentProps, type Route} from "@tanstack/react-router";
import {ErrorPageScaffold} from "~/components/pageScaffolds/errorPageScaffold";
import {useIntellsysDevtoolsConfiguration} from "~/contexts/intellsysDevtoolsClientContext";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeText} from "@vesta/ananke/components/anankeText";

export function NonEmbeddedErrorComponent({
    error,
    info,
    reset,
    route,
}: ErrorComponentProps & {
    route: Route;
}) {
    // #region Adding this to debug a weird error thingy
    console.error("a29ad0a1-bf0f-4c2e-b2de-66642bb3d540");

    console.groupCollapsed(+new Date(), "errorComponentLogs", route.id, "errorComponent");
    console.log("error", error);
    console.log("info", info);
    console.log("reset", reset);
    console.trace();
    console.groupEnd();
    // #endregion

    const intellsysDevtoolsConfiguration = useIntellsysDevtoolsConfiguration();

    if (intellsysDevtoolsConfiguration.insaneLogs.tanstackRouterDebugging === true) {
        console.info(+new Date(), "tanstackRouterDebugging", route.id, "errorComponent");
    }

    if (intellsysDevtoolsConfiguration.insaneLogs.errorComponentLogs === true) {
        console.groupCollapsed(+new Date(), "errorComponentLogs", route.id, "errorComponent");
        console.log("error", error);
        console.log("info", info);
        console.log("reset", reset);
        console.groupEnd();
    }

    const errorMessage = error instanceof Error ? error.message : String(error);

    if (error.message.startsWith("401")) {
        return (
            <ErrorComponentInternal>
                <div className="h-full grid place-items-center content-center flex-1">
                    <AnankeHeading size="large">You are not authenticated to access this page.</AnankeHeading>
                    <AnankeText>Please log in to access this page.</AnankeText>

                    <Link
                        to="/u"
                        className="underline hover:text-ds-text-information duration-200 mt-4"
                    >
                        Log in
                    </Link>
                </div>
            </ErrorComponentInternal>
        );
    }

    if (error.message.startsWith("403")) {
        return (
            <ErrorComponentInternal>
                <div className="h-full grid place-items-center content-center flex-1">
                    <AnankeHeading size="large">You are not authorized to access this page.</AnankeHeading>

                    <AnankeText>Please contact your administrator to be granted access.</AnankeText>

                    <Link
                        to="/u"
                        className="underline hover:text-ds-text-information duration-200 mt-4"
                    >
                        Back to Home
                    </Link>
                </div>
            </ErrorComponentInternal>
        );
    }

    return (
        <ErrorComponentInternal>
            <div className="h-full grid place-items-center content-center flex-1">
                <AnankeHeading size="large">Something went wrong</AnankeHeading>

                <AnankeText>We have notified our team, they are on it.</AnankeText>

                <AnankeText>{errorMessage}</AnankeText>

                <Link
                    to="/u"
                    className="underline hover:text-ds-text-information duration-200 mt-4"
                >
                    Back to Home
                </Link>
            </div>
        </ErrorComponentInternal>
    );
}

function ErrorComponentInternal({children}: {children: React.ReactNode}) {
    return (
        <>
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                        :root {
                            --gj-signed-out-header-height: 4.375rem;
                            --gj-signed-out-header-height-mobile: 2.75rem;
                            --gj-signed-out-footer-height: 15rem;
                            --gj-signed-out-footer-height-mobile: 31.5rem;
                        }
                    `,
                }}
            />

            <ErrorPageScaffold>
                {children}
            </ErrorPageScaffold>
        </>
    );
}
